/**
 * The document's own background, kept on the active theme ([[BUGS #173]]).
 *
 * **Why this is a module and not two lines inside a `useEffect`.** It was two
 * lines inside a `useEffect`, and it set only `document.body` — which is a bug
 * that cannot be seen from that code alone. The reason is in
 * `public/index.html`: the anti-FOUC script paints **both** `documentElement`
 * and `body` from the stored theme before React runs. So `html` carries a
 * background from first paint, and an element with a background stops `body`'s
 * from propagating to the canvas. Update only `body` and the canvas keeps
 * whatever theme the page was LOADED with, for the life of the tab.
 *
 * That is what the owner saw: a page loaded in dark, switched to light, and a
 * strip of `#1e1e1e` under a white page wherever the content was too short to
 * cover the viewport. Measured on staging — `html` and `body` resolved to two
 * different colours at the same moment.
 *
 * Pulling it out here makes the invariant testable: **both elements, always,
 * together.** A test can hold that; a `useEffect` inside a 700-line component
 * cannot.
 */

/** The elements whose background paints the page canvas. Both, or neither. */
const canvasElements = (): HTMLElement[] => [document.documentElement, document.body];

/**
 * Paint the document canvas.
 *
 * `color` should come from the active MUI theme's `background.default`, never a
 * literal: the boot script and this call site drifted apart once already
 * (`#1a1a1a`/`#ffffff` here against `#1e1e1e`/`#f8f9fa` there), which produced a
 * second, subtler flash one frame after the first.
 */
export function applyDocumentBackground(color: string): void {
  if (!color) return;
  for (const el of canvasElements()) {
    el.style.backgroundColor = color;
  }
}

/** Hand the canvas back to the stylesheet. */
export function clearDocumentBackground(): void {
  for (const el of canvasElements()) {
    el.style.backgroundColor = '';
  }
}
